import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IBoton, TipoBotonMobile } from 'modelos/src';
import {
  BOTONERA_OPCIONES,
  BOTONERA_SPECS,
  BotoneraTemplateId,
  LOGIN_OPCIONES,
  LOGIN_SPECS,
  LoginTemplateId,
  textoHint,
} from '../../../../auxiliares/plantillas/plantillas-spec';
import {
  construirPreviewVM,
  PreviewVM,
} from '../../provisionador/preview/preview-vm';
import { crearLinkMenuGroup } from '../../cliente-form/cliente-form.model';
import { StorageService } from '../../../../auxiliares/servicios/storage.service';
import { ICONO_LINK_DEFAULT, ICONOS_LINK } from './links-menu-iconos';
import { OPCIONES_ICONO_SIRENA, svgPinSirena } from './iconos-sirena-mapa';
import { primarioApp } from '../../../../auxiliares/plantillas/colores-app';

/**
 * Tab "App Nativa": TODO lo que consume la app nativa (white-label).
 * Identidad, branding, instructivo, pantalla de permisos, menú mobile,
 * módulos y EULA. Sumar un cliente = flavor en gradle + íconos + esta tab.
 * Recibe el formulario raíz por referencia: edita los mismos controles.
 */
@Component({
  selector: 'app-tab-app-nativa',
  templateUrl: './tab-app-nativa.component.html',
  styleUrls: ['./tab-app-nativa.component.scss'],
  standalone: false,
})
export class TabAppNativaComponent {
  @Input() public formulario?: UntypedFormGroup;
  /** Reservado: true en edición (lo pasa el padre). Los bundleIds quedan
   *  editables siempre — un valor mal cargado se puede corregir/borrar. */
  @Input() public esEdicion = false;
  /** Botones del cliente (para mostrarlos en el preview). Si no hay, se usan placeholders. */
  @Input() public botones?: IBoton[];

  /** Categorías editables del cliente (para <app-categoria-editor>). Solo se muestran si es genérico. */
  @Input() public categoriasEditables: any[] = [];
  /** Botones de "Servicios o Trámites", en orden. Se muta in-place (ver cliente-form.component.ts). */
  @Input() public idsBtnsServiciosTramites: string[] = [];

  /** La subida de la fuente la maneja el padre (loading + servicio). */
  @Output() public subirFuente = new EventEmitter<Event>();

  public loginOpciones = LOGIN_OPCIONES;
  public botoneraOpciones = BOTONERA_OPCIONES;

  /** Nombre del cliente (para la ruta de subida de imágenes en los editores de categoría). */
  get nombreCliente(): string {
    return this.formulario?.get('nombre')?.value || '';
  }
  get sirenas(): boolean {
    return !!this.formulario?.get('sirenas')?.value;
  }

  /** El cliente usa una plantilla GENÉRICA (grid/banner/…) → se puede previsualizar/elegir botonera. */
  get clienteEsGenerico(): boolean {
    const tpl = this.formulario?.get('inicioTemplate')?.value;
    return this.botoneraOpciones.some((o) => o.id === tpl);
  }
  /** Cliente custom: template hand-made por bundleId (inicioTemplate no es genérico). */
  get clienteEsCustom(): boolean {
    return !this.clienteEsGenerico;
  }
  /** Id del template del cliente (para mostrarlo en el cartel de custom). */
  get inicioTemplateCliente(): string {
    return this.formulario?.get('inicioTemplate')?.value || '(por bundleId)';
  }

  /** VM para el preview en vivo. Muestra los botones reales del cliente; si no hay, el preview
   *  usa su contenido de muestra por plantilla (no forzamos placeholders acá). */
  get previewVm(): PreviewVM {
    const vm = construirPreviewVM(this.formulario?.value ?? {}, [], [], []);
    vm.principales = (this.botones ?? []).map((b) => ({
      nombre: b.nombre,
      texto: b.texto,
      funcion: b.funcion,
      color: b.color,
      urlImagen: b.urlImagen,
    }));
    return vm;
  }

  /** Tamaño recomendado de cada imagen, según la plantilla elegida. '' = sin hint. */
  public hintImg(control: string): string {
    const loginTpl = (this.formulario?.get('loginTemplate')?.value ||
      'default') as LoginTemplateId;
    const botoneraTpl = (this.formulario?.get('inicioTemplate')?.value ||
      'grid') as BotoneraTemplateId;
    switch (control) {
      case 'icono':
        return '512×512 px (1:1)';
      case 'lateral':
        return '~512×512 px (flexible)';
      case 'login':
        return (
          textoHint(
            LOGIN_SPECS[loginTpl]?.slots.find((s) => s.slot === 'login'),
          ) || '1080×720 px'
        );
      case 'fondo':
        return '1242×2688 px (pantalla)';
      case 'arriba':
        return (
          textoHint(
            BOTONERA_SPECS[botoneraTpl]?.slots.find((s) => s.slot === 'arriba'),
          ) || '1080×540 px'
        );
      case 'abajo':
        return '1080×360 px';
      case 'sirena':
      case 'reflector':
        return '288×288 px (1:1)';
      case 'adelante':
      case 'atras':
        return '1080×216 px';
      case 'instructivo1':
      case 'instructivo2':
      case 'instructivo3':
        return '1080×1080 px (1:1)';
      case 'permisoUbicacion':
      case 'permisoCamara':
      case 'permisoMicrofono':
      case 'permisoNotificaciones':
        return '1080×1080 px (1:1)';
      default:
        return '';
    }
  }

  public botonesMobile: TipoBotonMobile[] = [
    'Vacio',
    'Ayuda',
    'Alertas',
    'Alertas Grupo',
    'Cobertura',
    'Contactos',
    'Eventos',
    'Eventos Grupo',
    'Líneas de Colectivos',
    'Notificaciones',
    'Perfil',
    'Reclamos',
    'Tramites',
    'Turnos',
  ];

  get configuracion(): UntypedFormGroup {
    return this.formulario?.get('configuracion') as UntypedFormGroup;
  }

  get imagenes(): UntypedFormGroup {
    return this.formulario?.get('imagenes') as UntypedFormGroup;
  }

  private storage = inject(StorageService);
  private sanitizer = inject(DomSanitizer);

  /** Claves de ícono que entiende la app nativa (espejo de LinksMenu.kt). */
  public readonly iconosLink = Object.keys(ICONOS_LINK);
  /** SVG de cada clave para la vista previa. Constantes propias: se marcan seguras una sola vez. */
  private readonly svgs: Record<string, SafeHtml> = Object.fromEntries(
    [...Object.entries(ICONOS_LINK), ['', ICONO_LINK_DEFAULT]].map(([k, cuerpo]) => [
      k,
      this.sanitizer.bypassSecurityTrustHtml(
        `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ` +
          `stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${cuerpo}</svg>`,
      ),
    ]),
  );
  /** Fila cuya imagen se está subiendo (por control, no por índice: sobrevive a reordenar). */
  public subiendoLink: AbstractControl | null = null;

  public svgIcono(clave: string | null | undefined): SafeHtml {
    return this.svgs[clave ?? ''] ?? this.svgs[''];
  }

  /** Ícono de las sirenas en Cobertura (`configuracion.iconoSirenaMapa`). */
  public readonly opcionesIconoSirena = OPCIONES_ICONO_SIRENA;
  /** Vista previa de cada opción. Constantes propias: se marcan seguras una sola vez. */
  private readonly svgsSirena: Record<string, SafeHtml> = Object.fromEntries(
    OPCIONES_ICONO_SIRENA.map((o) => [
      o.valor,
      this.sanitizer.bypassSecurityTrustHtml(svgPinSirena(o.valor)),
    ]),
  );

  public svgSirena(valor: string | null | undefined): SafeHtml {
    return this.svgsSirena[valor ?? 'default'] ?? this.svgsSirena['default'];
  }

  get nombreIconoSirena(): string {
    const valor = this.configuracion?.get('iconoSirenaMapa')?.value ?? 'default';
    return this.opcionesIconoSirena.find((o) => o.valor === valor)?.nombre ?? valor;
  }

  get iconoSirenaCustom(): boolean {
    return this.configuracion?.get('iconoSirenaMapa')?.value === 'custom';
  }

  /** null en el form viejo (crear-editar-cliente) que no tiene el array: la sección no se muestra. */
  get linksMenu(): UntypedFormArray | null {
    return (this.configuracion?.get('linksMenuMobile') as UntypedFormArray) ?? null;
  }

  public agregarLink(): void {
    this.linksMenu?.push(crearLinkMenuGroup());
    this.linksMenu?.markAsDirty();
  }

  public quitarLink(i: number): void {
    this.linksMenu?.removeAt(i);
    this.linksMenu?.markAsDirty();
  }

  public moverLink(i: number, delta: -1 | 1): void {
    const arr = this.linksMenu;
    const j = i + delta;
    if (!arr || j < 0 || j >= arr.length) return;
    const fila = arr.at(i);
    arr.removeAt(i);
    arr.insert(j, fila);
    arr.markAsDirty();
  }

  public async subirImagenLink(i: number, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    const fila = this.linksMenu?.at(i);
    if (!file || !fila) return;
    this.subiendoLink = fila;
    try {
      const res = await this.storage.subirImagenCliente(file, this.nombreCliente, `link-${Date.now()}`);
      const idx = this.linksMenu?.controls.indexOf(fila) ?? -1;
      if (idx >= 0) this.setImagenLink(idx, res.url);
    } finally {
      this.subiendoLink = null;
      input.value = '';
    }
  }

  public setImagenLink(i: number, url: string | null): void {
    const ctrl = this.linksMenu?.at(i).get('imagen');
    ctrl?.patchValue(url);
    ctrl?.markAsDirty();
  }

  public nombresValido(): boolean {
    return !!this.formulario?.get('nombre')?.value;
  }

  /** Tono con el que la app pinta el color del tema (primario Material 3 de esa semilla). */
  public tonoApp(colorTema?: string | null): string {
    return primarioApp(colorTema);
  }

  public onColorChange(event: string, control: string): void {
    const ctrl = this.configuracion?.get(control);
    ctrl?.patchValue(event);
    ctrl?.markAsDirty();
  }
}
